import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { AppState } from '../../store';
import { selectAccountId } from '../../account/store/account.selectors';
import { AnyConnection } from '../connection.models';
import { QueryParams } from '../../models/http.models';
import { environment } from '../../../environments/environment';
import { ListParams, trimObjectValues } from '../../common/helper/query-params-generic-list.helper';

@Injectable({
  providedIn: 'root',
})
export class ConnectionsResource {
  private apiRoot: string = `${environment.API_URL}`;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
  ) {}

  get(connection_id: number, params?: ListParams): Observable<AnyConnection> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<AnyConnection>(`${this.apiRoot}/${account_id}/api/connections/${connection_id}`, {
          params: params as QueryParams,
        }),
      ),
    );
  }

  save(body: Partial<AnyConnection>, params?: ListParams): Observable<AnyConnection> {
    const trimmedBody = trimObjectValues(body);
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.post<AnyConnection>(`${this.apiRoot}/${account_id}/api/connections`, trimmedBody, {
          params: params as QueryParams,
        }),
      ),
    );
  }

  update(connection_id: number, body: Partial<AnyConnection>, params?: ListParams): Observable<AnyConnection> {
    const trimmedBody = trimObjectValues(body);
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.put<AnyConnection>(`${this.apiRoot}/${account_id}/api/connections/${connection_id}`, trimmedBody, {
          params: params as QueryParams,
        }),
      ),
    );
  }

  remove(connection_id: number, params?: ListParams): Observable<any> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.delete<AnyConnection>(`${this.apiRoot}/${account_id}/api/connections/${connection_id}`, {
          params: params as QueryParams,
        }),
      ),
    );
  }

  query(params?: ListParams): Observable<AnyConnection[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<AnyConnection[]>(`${this.apiRoot}/${account_id}/api/connections`, {
          params: params as QueryParams,
        }),
      ),
    );
  }

  search(params?: ListParams): Observable<AnyConnection[]> {
    return this.store.pipe(
      selectAccountId,
      mergeMap((account_id) =>
        this.http.get<AnyConnection[]>(`${this.apiRoot}/${account_id}/api/connections/search`, {
          params: params as QueryParams,
        }),
      ),
    );
  }
}
